import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import type { RouteObject } from 'react-router';

import { ActiveVersionsDrilldownPage } from 'src/pages/activeVersionsPage/ActiveVersionsDrilldownPage';
import { ActiveVersionsPage } from 'src/pages/activeVersionsPage/ActiveVersionsPage';
import { DashboardPage } from 'src/pages/dashboard-page/DashboardPage';
import { InstanceDetailPage } from 'src/pages/instance-detail-page/InstanceDetailPage';
import { InstancesPage } from 'src/pages/instances-page/InstancesPage';
import { NotFoundPage } from 'src/pages/not-found-page/NotFoundPage';
import { SupportPackagesPage } from 'src/pages/support-packages-page/SupportPackagesPage';
import { UsersPage } from 'src/pages/users-page/UsersPage';
import { ROUTES } from 'src/utils/strings';

import { i18n } from './use-imago-mp-routes.i18n';

export type BreadcrumbCrumb = { label: MessageDescriptor; to?: string };

export type RouteDefinition = {
    id: string;
    path: string;
    title: MessageDescriptor;
    breadcrumbs: BreadcrumbCrumb[];
    element: ReactNode;
    children?: RouteDefinition[];
};

export const APP_ROUTES: readonly RouteDefinition[] = [
    {
        id: 'dashboard',
        path: ROUTES.ROOT_PATH,
        title: i18n.dashboard,
        breadcrumbs: [{ label: i18n.dashboard }],
        element: <DashboardPage />,
    },
    {
        id: 'instances',
        path: ROUTES.INSTANCES,
        title: i18n.instances,
        breadcrumbs: [{ label: i18n.dashboard, to: ROUTES.ROOT_PATH }, { label: i18n.instances }],
        element: <InstancesPage />,
    },
    {
        id: 'instance-detail',
        path: ROUTES.INSTANCE_DETAIL,
        title: i18n.instanceDetail,
        breadcrumbs: [
            { label: i18n.dashboard, to: ROUTES.ROOT_PATH },
            { label: i18n.instances, to: ROUTES.INSTANCES },
            { label: i18n.instanceDetail },
        ],
        element: <InstanceDetailPage />,
    },
    {
        id: 'users',
        path: ROUTES.USERS,
        title: i18n.users,
        breadcrumbs: [{ label: i18n.dashboard, to: ROUTES.ROOT_PATH }, { label: i18n.users }],
        element: <UsersPage />,
    },
    {
        id: 'support-packages',
        path: ROUTES.SUPPORT_PACKAGES,
        title: i18n.supportPackages,
        breadcrumbs: [
            { label: i18n.dashboard, to: ROUTES.ROOT_PATH },
            { label: i18n.supportPackages },
        ],
        element: <SupportPackagesPage />,
    },
    {
        id: 'active-versions',
        path: ROUTES.ACTIVE_VERSIONS,
        title: i18n.activeVersions,
        breadcrumbs: [
            { label: i18n.dashboard, to: ROUTES.ROOT_PATH },
            { label: i18n.activeVersions },
        ],
        element: <ActiveVersionsPage />,
    },
    {
        id: 'active-versions-drilldown',
        path: ROUTES.ACTIVE_VERSIONS_DRILLDOWN,
        title: i18n.activeVersionsDrilldown,
        breadcrumbs: [
            { label: i18n.dashboard, to: ROUTES.ROOT_PATH },
            { label: i18n.activeVersions, to: ROUTES.ACTIVE_VERSIONS },
            { label: i18n.activeVersionsDrilldown },
        ],
        element: <ActiveVersionsDrilldownPage />,
    },
];

const toRouteObject = (route: RouteDefinition): RouteObject => ({
    id: route.id,
    path: route.path,
    element: route.element,
});

export const useImagoMpRoutes = () => {
    const intl = useIntl();

    const guardedRoutes = useMemo(
        (): RouteObject[] => [
            ...APP_ROUTES.map(toRouteObject),
            {
                id: 'not-found',
                path: '*',
                element: (
                    <NotFoundPage
                        title={intl.formatMessage(i18n.notFoundTitle)}
                        message={intl.formatMessage(i18n.notFoundMessage)}
                    />
                ),
            },
        ],
        [intl],
    );

    return useMemo(
        () => ({
            guardedRoutes,
        }),
        [guardedRoutes],
    );
};
